import { get } from 'svelte/store';
import tippy from 'tippy.js';
import { t } from '../../i18n/i18n';
// import Popover from './Popover.svelte';

export function process_tooltip(node: HTMLElement, user_state: any, item_config: any) {
  const tooltipId = node.dataset.tooltipId!;
  const state = user_state?.[tooltipId];
  const config = item_config?.[tooltipId];

  if (!tooltipId || !state || !config) {
    return;
  }

  const tooltip_content = get(t)(config.tooltip?.content);

  const isTooltipOpen = () =>
    node.dataset.tooltipState === 'open' && !(config.tooltip?.click_to_dismiss && state.clicked);

  let instance = initializeAiTooltipTippy(node, tooltipId, tooltip_content, isTooltipOpen());

  // if click_to_dismiss ("click to exit"), get exit button
  if (isTooltipOpen() && config.tooltip?.click_to_dismiss) {
    const buttonHook = instance.popper?.querySelector('.tippy-exit-btn');
    if (buttonHook) {
      buttonHook.addEventListener('click', close);
    }
  }

  function close(e) {
    e.preventDefault();
    instance.hide();
    state.clicked = true;
    window.dispatchEvent(new CustomEvent('newsUpdate', { detail: { newsItemId: tooltipId } }));
    // reenable tippy in hover mode
    instance = initializeAiTooltipTippy(node, tooltipId, tooltip_content, isTooltipOpen());
  }

  // if tooltip should have badge, search hook for badge and inject
  if (config.tooltip?.badge && isTooltipOpen()) {
    const badgeHook = instance.popper?.querySelector(`[data-badge-hook="${tooltipId}"]`);
    if (badgeHook) {
      badgeHook.innerHTML = get(t)(config.tooltip?.badge);
      if (config.tooltip?.badgeSuffix) {
        badgeHook.innerHTML += config.tooltip?.badgeSuffix;
      }
    }
  }
}

function initializeAiTooltipTippy(
  node: HTMLElement,
  tooltipId: string,
  content: string = '<div>Content</div>',
  isTooltipOpen: boolean = false,
) {
  const badge = `<div data-badge-hook="${tooltipId}" style="color: #58C079; line-height: 20px;"></div>`;
  const exit_btn = `<button role="button" class="tippy-exit-btn" onclick="close" style="position: absolute; top: 0; right: 0; width: 20px; aspect-ratio: 1/1; display: grid; place-items: center; background: none; border: none; cursor: pointer; color: #D9D9D9;"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M0.183058 0.183058C0.427136 -0.0610194 0.822864 -0.0610194 1.06694 0.183058L4.79167 3.90778L8.51639 0.183058C8.76047 -0.0610194 9.1562 -0.0610194 9.40027 0.183058C9.64435 0.427136 9.64435 0.822864 9.40027 1.06694L5.67555 4.79167L9.40027 8.51639C9.64435 8.76047 9.64435 9.1562 9.40027 9.40027C9.1562 9.64435 8.76047 9.64435 8.51639 9.40027L4.79167 5.67555L1.06694 9.40027C0.822864 9.64435 0.427136 9.64435 0.183058 9.40027C-0.0610194 9.1562 -0.0610194 8.76047 0.183058 8.51639L3.90778 4.79167L0.183058 1.06694C-0.0610194 0.822864 -0.0610194 0.427136 0.183058 0.183058Z" fill="#D9D9D9"/>
</svg>
</button>`;

  const ref = tippy(node, {
    allowHTML: true,
    hideOnClick: false,
    showOnCreate: isTooltipOpen,
    content: `<div style="position: relative; display: flex; flex-direction: column;gap: 6px;">${isTooltipOpen ? badge : ''}<div>${content}</div>${isTooltipOpen ? exit_btn : ''}</div>`,
    interactive: true,
    placement: 'bottom-end',
    animation: 'scale-extreme',
    theme: 'mmttip',
    maxWidth: 375,
    offset: [0, 10],
    trigger: isTooltipOpen ? 'manual' : 'mouseenter focus',
  });
  return ref;
}

export function process_news_badge(
  node: HTMLElement,
  newsItemId: string,
  user_state: any,
  item_config: any,
) {
  // date based expiration
  if (item_config?.[newsItemId]?.validUntil) {
    const deadline = new Date(item_config?.[newsItemId]?.validUntil);
    const now = new Date();
    if (deadline < now) {
      node.remove();
      return;
    }
  }

  // Interaction Observer: when fully in view, dispatch newsUpdate
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // destroy observer
        observer.disconnect();
        // dispatch newsUpdate
        window.dispatchEvent(new CustomEvent('newsUpdate', { detail: { newsItemId } }));
      }
    });
  });

  observer.observe(node);

  const max_exposures = item_config?.[newsItemId]?.max_exposures;

  if (!isNaN(max_exposures) && user_state?.[newsItemId]?.seen >= max_exposures) {
    // remove node from DOM
    node.remove();
    return;
  }

  /** HINT: for config-driven top-down curation, that'd be a way to cast / add badges
   * node.innerText = "I'm a Svelte Badger Collaborator";
   **/

  // check if it has a tooltip
  // HINT: currently not used

  // const has_tooltip = node.dataset.dataTooltipState;

  // node.setAttribute('data-tooltip-state', 'open');
  // if (has_tooltip) {
  //   const popover_ref = append_tooltip(node);
  //   return popover_ref;
  // }

  return null;
}

// TODO: style future Popover component or re-use existing Tooltip component
// TODO: consider passing content via data attributes or global news config
// HINT: currently not used
// function append_tooltip(node: HTMLElement) {
//   const popover_ref = new Popover({
//     target: node,
//     props: { tooltip: "I'm a Svelte Popover inside Sirius" },
//   });

//   const button = node.querySelector('button');
//   if (button) button.innerHTML = 'ℹ️';
//   return popover_ref;
// }

// DB might return incomplete state; enforce template for single constraints
export function enforce_news_template(
  state: { news: Record<string, { clicked?: boolean; seen?: number }> },
  item_ids: string[],
  templates: Record<string, { clicked?: boolean; seen?: number }>,
) {
  // for each news item, enforce
  item_ids.forEach((item_id) => {
    const item = state.news[item_id];

    // if no item, apply template for whole item
    if (!item) {
      console.log('no state; enforcing', templates[item_id]);
      state.news[item_id] = { ...templates[item_id] };
      return;
    }

    // if item exists, inspect each constraint
    if (item) {
      const defaults = templates[item_id];
      if (defaults) {
        for (const constraint in defaults) {
          if (!item[constraint]) {
            item[constraint] = defaults[constraint];
          }
        }
      }
      state.news[item_id] = { ...item };
    }
  });

  // return guaranteed valid state
  return state;
}
